// Promos de venta de Inventario a la forma de `@/shared/patterns/Promotions`: la tabla solo trae
// nombres; la ficha del SKU trae beneficio, vigencia, segmentación y alcance.
import type {
    ActiveSalePromotionSummary,
    InventorySkuDetail,
} from '@/api/services/retail-catalog/backoffice';
import {
    formatPromotionValidity,
    type PromotionField,
    type PromotionSummary,
    type PromotionView,
    type PromotionsTabState,
} from '@/shared/patterns/Promotions';
import { describeScope, describeSegmentation } from '../../promotions/lib/promotionsList';
import { formatLevers } from '../../promotions/lib/promotionsRow';
import type { InventoryRow } from './inventoryList';

export const SKU_PROMOTIONS_UNAVAILABLE =
    'Las promociones de venta no se pudieron leer para este SKU; reintenta en un momento.';
export const LIST_PROMOTIONS_UNAVAILABLE =
    'Las promociones de venta no se pudieron leer: la tabla no marca qué SKUs tienen promo.';
export const LIST_UNDETAILED_PROMOTION =
    'El inventario marca una promoción de venta, pero no trae su nombre.';
export const LIST_BENEFIT_HINT = 'detalle en la pestaña Promos de la ficha';
export const SEGMENT_NAME_GAP = 'La ficha trae el id del segmento, no su nombre.';

const NO_SEGMENT_NAMES: ReadonlyMap<string, string> = new Map();

/** Las promos de la fila para la píldora; los nombres van en orden alfabético. */
export function rowPromotionSummaries(
    row: Pick<InventoryRow, 'promotionNames'>,
): PromotionSummary[] {
    return (row.promotionNames ?? []).map((name) => ({
        id: name,
        name,
        benefits: [LIST_BENEFIT_HINT],
    }));
}

/** El lente marca promo sin nombrarla: la píldora dice «Promo» con el motivo. */
export function rowUndetailedPromotion(
    row: Pick<InventoryRow, 'hasPromotion' | 'promotionNames'>,
): string | undefined {
    return row.hasPromotion === true && (row.promotionNames ?? []).length === 0
        ? LIST_UNDETAILED_PROMOTION
        : undefined;
}

function audienceOf(promotion: ActiveSalePromotionSummary): PromotionField {
    const segmentIds = promotion.segmentation.segmentIds ?? [];
    if (segmentIds.length === 0) {
        return { text: describeSegmentation(promotion, NO_SEGMENT_NAMES) };
    }
    const count = `${String(segmentIds.length)} ${segmentIds.length === 1 ? 'segmento' : 'segmentos'}`;
    const credit = promotion.segmentation.requiresActiveSurekeepCredit ? ' · con crédito SK' : '';
    return { text: `${count}${credit}`, missing: SEGMENT_NAME_GAP };
}

/** Una promo de la ficha a su tarjeta; sin resumen del silo, el beneficio sale de sus palancas. */
export function salePromotionView(promotion: ActiveSalePromotionSummary): PromotionView {
    const summary = promotion.benefitSummary.trim();
    return {
        id: promotion.promotionId,
        name: promotion.name,
        benefits: [summary === '' ? formatLevers(promotion) : summary],
        validity: { text: formatPromotionValidity(promotion.validFrom, promotion.validUntil) },
        audience: audienceOf(promotion),
        scope: { text: describeScope(promotion.scope) },
    };
}

/** Contenido de la pestaña Promos con la primera página de la ficha. */
export function skuPromotionsState(
    sheet: InventorySkuDetail | undefined,
    isPending: boolean,
    error: unknown,
): PromotionsTabState {
    if (error) return { kind: 'noSource', reason: SKU_PROMOTIONS_UNAVAILABLE };
    if (isPending || sheet === undefined) return { kind: 'loading' };
    const promotions = sheet.promotions;
    if (sheet.sources?.promotions === 'UNAVAILABLE' || promotions == null) {
        return { kind: 'noSource', reason: SKU_PROMOTIONS_UNAVAILABLE };
    }
    return { kind: 'ready', promotions: promotions.map(salePromotionView) };
}
